import useSWR from 'swr';
import { sdk } from '@/sdk';
import { Permission } from '@shared/types/business-unit/Associate';

const useAccountRoles = (businessUnitKey?: string) => {
  // EWM-644 — a single observer keyed by '/action/business-unit/getAssociate', which
  // AppProviders seeds server-side from viewer.associate. With the provider's
  // revalidateIfStale:false, a populated seed means NO getAssociate fires on load. This hook
  // previously ran a SECOND, BU-keyed observer that was never seeded and so fired a redundant
  // getAssociate on every load — dropped here. A customer has exactly one immutable business
  // unit (one-BU-per-customer), so the seeded associate is authoritative; the fetcher still
  // passes businessUnitKey so any revalidation resolves the correct BU.
  //
  // A logged-in customer whose associate can't be resolved (misconfigured in commercetools —
  // no associate / no roles / not attached to a BU) is rejected at login and told to contact
  // customer service; it is deliberately NOT papered over here with a client-side retry.
  const response = useSWR('/action/business-unit/getAssociate', () =>
    sdk.composableCommerce.businessUnit.getAssociate({ businessUnitKey: businessUnitKey as string }),
  );

  const defaultData = response.data?.isError ? null : (response.data?.data ?? null);

  const data = defaultData ?? {};

  const roles = data.roles ?? [];

  const isAdmin = roles.findIndex((role) => role.key === 'admin') !== -1;

  const rolePermissions = roles.reduce((acc, curr) => [...acc, ...(curr.permissions ?? [])], [] as Permission[]);

  const permissions = rolePermissions.reduce(
    (acc, permission) => ({
      ...acc,
      [permission]: true,
    }),
    {} as Record<Permission, boolean>,
  );

  // EWM-298 — expose the in-flight state so route guards (useRequireMaster) can wait for the
  // associate before deciding. `permissions` is empty until it resolves, which a naive check
  // reads as View-Only. On the happy path the seed resolves synchronously (isLoading false).
  const isLoading = response.isLoading;

  return { roles, isAdmin, permissions, isLoading };
};

export default useAccountRoles;
